import {
  Boxes,
  CalendarClock,
  ClipboardCheck,
  FileText,
  GitCompare,
  Layers,
  MoreHorizontal,
  Pencil,
  PlayCircle,
  Printer,
  RefreshCw,
  Send,
  Table2,
  Undo2,
  X,
} from "lucide-react";
import React, { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { Action, ActionKey, StageActions as Actions } from "./ozonStage";

/** Одно главное действие стадии и всё остальное — под «…».
 *
 *  ⚠️ Кнопок у поставки полтора десятка, и раньше они стояли в ряд все сразу: нужная терялась
 *  среди аварийных («Перевыпустить места», «Сбросить цикл 1С»), а какая нужна СЕЙЧАС — экран не
 *  говорил. Набор считает `actionsFor`, здесь только разметка.
 */
const ICONS: Record<ActionKey, React.ComponentType<{ className?: string }>> = {
  draft: Send,
  slots: CalendarClock,
  resolve: RefreshCw,
  compute: Boxes,
  recompute: Boxes,
  send: Send,
  labels: Printer,
  pallets: Layers,
  packing: ClipboardCheck,
  packing_xlsx: Table2,
  onec_run: FileText,
  onec_reset: RefreshCw,
  reissue: RefreshCw,
  cancel: X,
  change: GitCompare,
  change_start: Pencil,
  change_abort: Undo2,
  change_apply: PlayCircle,
  labels_changed: Printer,
};

export function StageActions({
  actions,
  busy,
  pending,
  active,
  onAction,
}: {
  actions: Actions;
  /** идёт любой вызов по этой поставке — гасим всё, чтобы не запустить второй */
  busy: boolean;
  /** крутить иконку именно у нажатого действия */
  pending: (key: ActionKey) => boolean;
  /** действие, чей раздел сейчас раскрыт (окна приёмки, превью правки) — подсвечиваем кнопку */
  active?: ActionKey | null;
  onAction: (key: ActionKey) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const { primary, more } = actions;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {primary && (
        <Button
          size="sm"
          disabled={busy || primary.disabled !== undefined}
          title={primary.disabled ?? primary.hint}
          className={cn(active === primary.key && "ring-2 ring-ring")}
          onClick={() => onAction(primary.key)}
        >
          <Icon action={primary} spin={pending(primary.key)} />
          <Label action={primary} />
        </Button>
      )}
      {more.length > 0 && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              title="Остальные действия по этой поставке"
              aria-label="Остальные действия"
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 p-1">
            {more.map((a, i) => (
              <React.Fragment key={a.key}>
                {/* печать идёт своей группой: три файла подряд читаются как один блок */}
                {a.print && !more[i - 1]?.print && (
                  <div className="px-2 pb-0.5 pt-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
                    Печать
                  </div>
                )}
                <div className="flex items-center">
                  <button
                    type="button"
                    disabled={busy || a.disabled !== undefined}
                    title={a.disabled ?? a.hint}
                    className="flex flex-1 items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent disabled:opacity-50"
                    onClick={() => {
                      setOpen(false);
                      onAction(a.key);
                    }}
                  >
                    <Icon action={a} spin={pending(a.key)} />
                    <Label action={a} />
                  </button>
                  {/* второй формат того же документа: кликается отдельно — два файла разом
                      браузер не отдаёт, второй он режет как непрошеную загрузку */}
                  {a.alt && (
                    <>
                      <span className="text-muted-foreground/50">·</span>
                      <button
                        type="button"
                        disabled={busy}
                        title={a.alt.hint}
                        className="flex items-center gap-1 rounded px-2 py-1.5 text-sm hover:bg-accent disabled:opacity-50"
                        onClick={() => {
                          setOpen(false);
                          onAction(a.alt!.key);
                        }}
                      >
                        <Icon action={a.alt} spin={pending(a.alt.key)} />
                        {a.alt.label}
                      </button>
                    </>
                  )}
                </div>
              </React.Fragment>
            ))}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

/** Название действия и, если оно сейчас недоступно, — чего не хватает. ⚠️ Подпись обязательна:
 *  погашенная кнопка без причины читается как поломка экрана. */
function Label({ action }: { action: Action }): React.ReactElement {
  return (
    <span>
      {action.label}
      {action.disabled !== undefined && (
        <span className="text-xs font-normal opacity-70">
          {" "}
          — {action.disabled}
        </span>
      )}
    </span>
  );
}

function Icon({
  action,
  spin,
}: {
  action: Action;
  spin: boolean;
}): React.ReactElement {
  const C = ICONS[action.key];
  return <C className={cn("size-4 shrink-0", spin && "animate-pulse")} />;
}
